import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { setPreviewToken, clearPreviewToken, getPreviewToken } from "../lib/previewAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editorial preview gate. The token is pasted here once per session, verified
// against the backend, and then held only in sessionStorage. It never appears
// in the URL bar and never leaves this tab.
export default function PreviewGate() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [status, setStatus] = useState({ type: null, message: "" });
  const [busy, setBusy] = useState(false);
  const alreadyIn = !!getPreviewToken();

  useEffect(() => {
    const t0 = document.title;
    document.title = "Preview — The Thriving Life 101";
    const meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex,nofollow");
    document.head.appendChild(meta);
    return () => { document.title = t0; document.head.removeChild(meta); };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setStatus({ type: null, message: "" });
    try {
      await axios.post(`${API}/preview/verify`, null, { headers: { "X-Preview-Token": token.trim() } });
      setPreviewToken(token.trim());
      setStatus({ type: "success", message: "Preview access granted." });
      setTimeout(() => navigate("/perspectives", { replace: true }), 400);
    } catch (err) {
      setStatus({ type: "error", message: "Token not recognised." });
    } finally {
      setBusy(false);
    }
  };

  const onExit = () => {
    clearPreviewToken();
    setStatus({ type: "success", message: "Preview session ended." });
    setTimeout(() => navigate("/", { replace: true }), 300);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="preview-gate">
      <div className="max-w-md w-full">
        <span className="text-[10px] font-sans tracking-[0.4em] uppercase text-[var(--accent-bronze)] font-semibold block text-center">
          Editorial Preview
        </span>
        <h1 className="mt-4 font-serif tracking-tight text-3xl md:text-4xl text-primary-custom leading-tight text-center">
          Enter preview access
        </h1>
        <p className="mt-4 font-serif italic text-secondary-custom text-center leading-relaxed">
          Draft Perspectives are protected. Paste the preview token to review unpublished essays for this tab only.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Preview token"
            autoComplete="off"
            spellCheck={false}
            data-testid="preview-token-input"
            className="w-full bg-transparent border border-subtle-custom focus:border-[var(--accent-bronze)] outline-none px-4 py-3 font-serif text-base text-primary-custom placeholder:text-secondary-custom/60 transition-colors"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="preview-token-submit"
            className="w-full py-3 text-[11px] font-sans tracking-[0.3em] uppercase text-primary-custom border border-primary-custom hover:bg-primary-custom hover:text-surface-custom transition-colors disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Unlock preview"}
          </button>
        </form>

        {status.message && (
          <p
            data-testid="preview-token-status"
            className={`mt-4 text-center text-sm font-serif italic ${status.type === "error" ? "text-red-600" : "text-secondary-custom"}`}
          >
            {status.message}
          </p>
        )}

        {alreadyIn && (
          <button
            onClick={onExit}
            data-testid="preview-exit"
            className="mt-8 w-full text-[10px] font-sans tracking-[0.3em] uppercase text-secondary-custom hover:text-[var(--accent-bronze)] transition-colors"
          >
            End preview session
          </button>
        )}
      </div>
    </div>
  );
}
